import "./ChatInteraction.css";
import "./ChatAnswerSections.css";
import { createEffect, createSignal, For, Show } from "solid-js";
import type { ContextAnswer } from "../../components/ContextAnswerPill.tsx";
import { QuestionBlock } from "../../components/QuestionBlock.tsx";
import type { InteractionState } from "../chatPanelState.ts";
import type { ChatPanelController } from "../useChatPanelController.ts";
import { ChatExceptionBlock } from "./ChatExceptionBlock.tsx";
import { ChatNotificationBlock } from "./ChatNotificationBlock.tsx";
import { ChatReasoningSection } from "./ChatReasoningSection.tsx";
import { ChatSpeakingCard } from "./ChatSpeakingCard.tsx";
import {
	EffortUpgradeCard,
	EffortUpgradeResolution,
} from "./EffortUpgradeCard.tsx";
import { GVTThinkingSymbol } from "@ui/typography/GVTThinkingSymbol.tsx";

declare const pageVars: {
	appEmbedded: boolean;
};

type Props = {
	interaction: InteractionState;
	questionId: string;
	projectId: string | null;
	renderingHistory: boolean;
	scrollToInteractionIndex?: number;
	controller: ChatPanelController;
	/** Whether this interaction is the one currently being reasoned about. */
	reasoning: boolean;
	contextAnswer?: ContextAnswer;
};

export const ChatInteraction = (props: Props) => {
	const interactionId = () => props.interaction.id ?? "";
	const [interactionEl, setInteractionEl] = createSignal<
		HTMLDivElement | undefined
	>();

	// Whether the `.response` div renders anything the user can actually see.
	// Derived from state (not the DOM) so it stays reactive and mirrors the
	// rendering rules below: an empty speaking block produces no visible output.
	const hasVisibleOutput = () => {
		const answer = props.interaction.answer;
		if (!answer) {
			return false;
		}
		return answer.responseBlocks.some(
			(block) => block.block.markdown.trim().length > 0,
		);
	};

	createEffect(() => {
		const targetIndex = props.scrollToInteractionIndex;
		const el = interactionEl();
		if (
			targetIndex === undefined ||
			props.interaction.index !== targetIndex ||
			!el
		) {
			return;
		}

		props.controller.clearQuestionScrollTarget();
		requestAnimationFrame(() => {
			el.scrollIntoView({ behavior: "smooth", block: "start" });
		});
	});

	return (
		<div
			class="interaction"
			id={props.interaction.id}
			data-interaction-index={props.interaction.index}
			data-query-context-id={props.interaction.queryContextId}
			ref={setInteractionEl}
		>
			<Show when={props.interaction.question}>
				<QuestionBlock
					prompt={props.interaction.question}
					appEmbedded={pageVars.appEmbedded}
					contextAnswer={props.contextAnswer}
				/>
			</Show>

			<Show when={props.interaction.exception}>
				<ChatExceptionBlock
					title={props.interaction.exception.title}
					message={props.interaction.exception.message}
					identifier={props.interaction.exception.identifier}
				/>
			</Show>

			<For each={props.interaction.ephemeralNotifications}>
				{(notification) => (
					<ChatNotificationBlock
						notification={notification}
						onRefresh={() => props.controller.refreshConversation()}
					/>
				)}
			</For>

			<Show when={props.interaction.answer}>
				{(answer) => (
					<div class="answer">
						<ChatReasoningSection
							answer={answer()}
							interactionIndex={props.interaction.index}
							renderingHistory={props.renderingHistory}
							reasoning={props.reasoning}
							hasVisibleOutput={hasVisibleOutput()}
							onToggleReasoning={(open) =>
								props.controller.setReasoningToggleOpen(
									props.interaction.index,
									open,
								)
							}
							onCloseReasoning={() =>
								props.controller.setReasoningEventsOpen(
									props.interaction.index,
									false,
								)
							}
							effortUpgradeResolution={
								<Show when={answer().effortUpgradeResolution}>
									{(resolution) => (
										<EffortUpgradeResolution
											resolution={resolution()}
											request={answer().effortUpgradeRequest}
										/>
									)}
								</Show>
							}
						/>
						<Show when={answer().effortUpgradeRequest} keyed>
							{(request) => (
								<EffortUpgradeCard
									questionId={props.questionId}
									interactionId={interactionId()}
									request={request}
									resolved={
										answer().effortUpgradeResolution?.requestId ===
										request.requestId
									}
									settled={
										answer().answerAvailable ||
										props.interaction.exception !== undefined
									}
								/>
							)}
						</Show>
						<div
							class="response"
							id={
								props.interaction.savedDocumentPublicId
									? `doc-${props.interaction.savedDocumentPublicId}`
									: undefined
							}
						>
							<For each={answer().responseBlocks}>
								{(block) => (
									<ChatSpeakingCard
										block={block.block}
										questionId={props.questionId}
										interactionId={interactionId()}
										projectId={props.projectId ?? undefined}
										artifactId={props.interaction.artifactId}
										documentId={props.interaction.savedDocumentPublicId}
										isFavourite={props.interaction.savedDocumentIsFavourite}
									/>
								)}
							</For>
						</div>
					</div>
				)}
			</Show>

			<Show when={props.reasoning && hasVisibleOutput()}>
				<GVTThinkingSymbol />
			</Show>
		</div>
	);
};
